/**
 * Минимальный генератор xlsx для режима предпросмотра.
 *
 * Настоящую книгу собирает бэкенд через openpyxl. Здесь бэкенда нет, а отдавать
 * csv под именем .xlsx нельзя — Excel отказывается открывать такой файл. Тянуть
 * ради предпросмотра exceljs (сотни килобайт в бандл) тоже незачем, поэтому
 * xlsx собирается вручную: это zip из пяти XML-частей.
 *
 * Сознательные упрощения: один лист, все значения строками (inlineStr), файлы
 * кладутся в архив без сжатия. Для фикстуры на несколько строк этого достаточно.
 */

const crcTable = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[i] = c >>> 0;
  }
  return table;
})();

const crc32 = (bytes: Uint8Array): number => {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    c = crcTable[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
};

type ZipEntry = { name: string; data: Uint8Array };

/** Складывает части в zip методом "store" — без сжатия, но по спецификации. */
const zip = (entries: ZipEntry[], mimeType: string): Blob => {
  const encoder = new TextEncoder();
  const parts: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;

  entries.forEach((entry) => {
    const name = encoder.encode(entry.name);
    const crc = crc32(entry.data);

    const local = new Uint8Array(30 + name.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true); // подпись локального заголовка
    lv.setUint16(4, 20, true); // минимальная версия
    lv.setUint32(14, crc, true);
    lv.setUint32(18, entry.data.length, true); // сжатый размер
    lv.setUint32(22, entry.data.length, true); // исходный размер
    lv.setUint16(26, name.length, true);
    local.set(name, 30);

    parts.push(local, entry.data);

    const record = new Uint8Array(46 + name.length);
    const cv = new DataView(record.buffer);
    cv.setUint32(0, 0x02014b50, true); // подпись записи каталога
    cv.setUint16(4, 20, true);
    cv.setUint16(6, 20, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, entry.data.length, true);
    cv.setUint32(24, entry.data.length, true);
    cv.setUint16(28, name.length, true);
    cv.setUint32(42, offset, true); // смещение локального заголовка
    record.set(name, 46);

    central.push(record);
    offset += local.length + entry.data.length;
  });

  const centralSize = central.reduce((sum, record) => sum + record.length, 0);

  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, 0x06054b50, true); // подпись конца каталога
  ev.setUint16(8, entries.length, true);
  ev.setUint16(10, entries.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, offset, true);

  return new Blob([...parts, ...central, end], { type: mimeType });
};

const escapeXml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** A, B, ... Z, AA, AB — адресация колонок в книге. */
const columnName = (index: number): string => {
  let name = "";
  let n = index;
  do {
    name = String.fromCharCode(65 + (n % 26)) + name;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return name;
};

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * Собирает книгу из таблицы строк. Первая строка — заголовки, но специально
 * она не размечается: для фикстуры хватает обычных ячеек.
 */
export const buildPreviewXlsx = (
  rows: string[][],
  sheetName = "Sheet1",
): Blob => {
  const encoder = new TextEncoder();

  const sheetRows = rows
    .map((row, rowIndex) => {
      const cells = row
        .map(
          (value, cellIndex) =>
            `<c r="${columnName(cellIndex)}${rowIndex + 1}" t="inlineStr">` +
            `<is><t>${escapeXml(value)}</t></is></c>`,
        )
        .join("");
      return `<row r="${rowIndex + 1}">${cells}</row>`;
    })
    .join("");

  const files: Record<string, string> = {
    "[Content_Types].xml":
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
      '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
      '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
      '<Default Extension="xml" ContentType="application/xml"/>' +
      '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
      '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' +
      "</Types>",
    "_rels/.rels":
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
      '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
      '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
      "</Relationships>",
    "xl/workbook.xml":
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
      '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ' +
      'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
      `<sheets><sheet name="${escapeXml(sheetName)}" sheetId="1" r:id="rId1"/></sheets>` +
      "</workbook>",
    "xl/_rels/workbook.xml.rels":
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
      '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
      '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>' +
      "</Relationships>",
    "xl/worksheets/sheet1.xml":
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
      '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
      `<sheetData>${sheetRows}</sheetData>` +
      "</worksheet>",
  };

  return zip(
    Object.entries(files).map(([name, xml]) => ({
      name,
      data: encoder.encode(xml),
    })),
    XLSX_MIME,
  );
};
